import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { lotteryAPI, authAPI } from '../services/api';
import BuySharesModal from './BuySharesModal';
import './ClubFund.css';

const GAME_COLORS = {
  'powerball': '#f0f0f0',
  'oz_lotto': '#f0f0f0',
  'saturday_lotto': '#f0f0f0',
  'weekly_windfall': '#f0f0f0',
  'set_for_life': '#f0f0f0',
  'default': '#f0f0f0',
};

const GAME_LOGOS = {
  'powerball': '/images/lotto/powerball.png',
  'oz_lotto': '/images/lotto/oz_lotto.png',
  'saturday_lotto': '/images/lotto/saturday_lotto.png',
  'weekly_windfall': '/images/lotto/weekly-windfall.svg',
  'set_for_life': '/images/lotto/set_for_life.png',
};

function getGameColor(gameCode) {
  return GAME_COLORS[gameCode] || GAME_COLORS.default;
}

function getGameLogo(gameCode) {
  return GAME_LOGOS[gameCode] || null;
}

function ClubLotto({ clubId: propClubId, embedded = false }) {
  const params = useParams();
  const clubId = propClubId || params.clubId;
  const [draws, setDraws] = useState([]);
  const [myShares, setMyShares] = useState([]);
  const [history, setHistory] = useState([]);
  const [availableGames, setAvailableGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('draws');
  const [buyModalDraw, setBuyModalDraw] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCaptain, setIsCaptain] = useState(false);
  const [showCreateDraw, setShowCreateDraw] = useState(false);
  const [createDrawData, setCreateDrawData] = useState({ gameCode: '', drawDate: '' });
  const [createDrawLoading, setCreateDrawLoading] = useState(false);
  const [createDrawError, setCreateDrawError] = useState('');
  const [expandedGames, setExpandedGames] = useState({});

  const formatCurrency = (cents) => `$${(cents / 100).toFixed(2)}`;
  const formatJackpot = (cents) => {
    const dollars = cents / 100;
    if (dollars >= 1000000) return `$${(dollars / 1000000).toFixed(dollars % 1000000 === 0 ? 0 : 1)}M`;
    if (dollars >= 1000) return `$${(dollars / 1000).toFixed(0)}K`;
    return `$${dollars.toFixed(0)}`;
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // First: get user (cached by browser)
      const userData = await authAPI.getCurrentUser().catch(() => null);
      if (userData?.user) setCurrentUser(userData.user);

      // Then: load lottery data in parallel (4 calls, not 6)
      const [drawsRes, sharesRes, historyRes, gamesRes] = await Promise.allSettled([
        lotteryAPI.getDraws(clubId),
        lotteryAPI.getMyShares(clubId),
        lotteryAPI.getHistory(clubId),
        lotteryAPI.getGames(),
      ]);
      const drawsData = drawsRes.status === 'fulfilled' ? drawsRes.value : {};
      setDraws(drawsData.upcoming || []);
      setMyShares(sharesRes.status === 'fulfilled' ? (sharesRes.value.shares || []) : []);
      setHistory(historyRes.status === 'fulfilled' ? (historyRes.value.history || []) : []);
      setAvailableGames(gamesRes.status === 'fulfilled' ? (gamesRes.value.games || []) : []);

      // Check captain from draws response metadata or club info
      if (userData?.user && drawsData.captainUserId) {
        setIsCaptain(drawsData.captainUserId === userData.user.id);
      }
    } catch (err) {
      console.error('Error loading lotto data:', err);
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateDraw = async (apiDraw) => {
    setCreateDrawLoading(true);
    setCreateDrawError('');
    try {
      await lotteryAPI.createDraw(clubId, {
        gameCode: apiDraw.gameCode,
        externalDrawId: apiDraw.drawId,
        gameName: apiDraw.gameName,
        drawDate: apiDraw.drawDate,
        drawNumber: apiDraw.drawNumber,
      });
      setShowCreateDraw(false);
      setCreateDrawData({ gameCode: '' });
      await loadData();
    } catch (err) {
      setCreateDrawError(err.response?.data?.error || 'Failed to create draw entry.');
    } finally {
      setCreateDrawLoading(false);
    }
  };

  const handleBuySuccess = () => {
    setBuyModalDraw(null);
    loadData();
  };

  const renderNumberBall = (num, isSupplementary = false) => (
    <span
      key={`${num}-${isSupplementary ? 's' : 'm'}`}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '30px', height: '30px', borderRadius: '50%', fontSize: '12px', fontWeight: '700',
        backgroundColor: isSupplementary ? '#6b7280' : '#f0c040',
        color: isSupplementary ? '#fff' : '#1a1d23',
      }}
    >
      {num}
    </span>
  );

  const renderProgressBar = (sold, total) => {
    const pct = total > 0 ? Math.min((sold / total) * 100, 100) : 0;
    return (
      <div style={{ height: '6px', backgroundColor: 'var(--border, #e2e8f0)', borderRadius: '3px', overflow: 'hidden', marginTop: '4px' }}>
        <div style={{ height: '100%', backgroundColor: '#f0c040', borderRadius: '3px', width: `${pct}%`, transition: 'width 0.3s' }} />
      </div>
    );
  };

  const renderDrawCard = (draw, compact = false) => {
    const gameColor = getGameColor(draw.gameCode);
    const totalShares = draw.maxShares || draw.totalShares || 10;
    const sold = draw.sharesSold || 0;
    const remaining = totalShares - sold;
    const past = draw.drawDate && new Date(draw.drawDate) < new Date();
    const full = remaining <= 0;

    return (
      <div key={draw.id || draw.drawId} style={{ backgroundColor: 'var(--surface, #1e293b)', borderRadius: '1rem', border: '1px solid var(--border, #334155)', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {!compact && (
          <div style={{ background: gameColor, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {getGameLogo(draw.gameCode) ? (
              <img src={getGameLogo(draw.gameCode)} alt={draw.gameName} style={{ height: '28px', objectFit: 'contain' }} />
            ) : (
              <span style={{ color: '#fff', fontSize: '15px', fontWeight: '700' }}>{draw.gameName || draw.gameCode || 'Draw'}</span>
            )}
          </div>
        )}
        <div style={{ padding: compact ? '10px 14px' : '14px' }}>
          <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '13px', marginBottom: '6px' }}>
            {draw.drawDate && new Date(draw.drawDate).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
            {draw.drawNumber && ` - Draw #${draw.drawNumber}`}
          </div>
          {(draw.jackpotCents || draw.jackpotEstimate) && (
            <div style={{ color: '#16a34a', fontSize: compact ? '13px' : '14px', fontWeight: '600', marginBottom: '8px' }}>
              Est. Jackpot: {formatJackpot(draw.jackpotCents || draw.jackpotEstimate)}
            </div>
          )}
          <div style={{ marginBottom: '8px' }}>
            <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '13px' }}>{sold} of {totalShares} shares sold</span>
            {renderProgressBar(sold, totalShares)}
          </div>
          {!compact && (
            <div style={{ fontSize: '12px', color: 'var(--text-secondary, #64748b)', marginBottom: '8px' }}>
              Pool: {formatCurrency(draw.poolCents || (sold * (draw.sharePriceCents || 500)))}
              {draw.gamePriceCents > 0 && ` = ${draw.calculatedGames || Math.floor((draw.poolCents || (sold * (draw.sharePriceCents || 500))) / (draw.gamePriceCents || 500))} game${(draw.calculatedGames || 0) !== 1 ? 's' : ''} @ ${formatCurrency(draw.gamePriceCents || 500)} each`}
            </div>
          )}
          {draw.numbersSelected && draw.numbersSelected.length > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '11px' }}>
                {draw.gamesCount || draw.numbersSelected.length} Game{(draw.gamesCount || draw.numbersSelected.length) !== 1 ? 's' : ''} Purchased:
              </span>
              {(Array.isArray(draw.numbersSelected[0]) ? draw.numbersSelected : [draw.numbersSelected]).map((game, gIdx) => (
                <div key={gIdx} style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '4px', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '10px', minWidth: '40px' }}>#{gIdx + 1}</span>
                  {game.map((n, nIdx) => <span key={nIdx} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: '50%', fontSize: 10, fontWeight: 700, backgroundColor: '#f0c040', color: '#1a1d23', margin: 1 }}>{n}</span>)}
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
            <span style={{ color: 'var(--text-primary, #0f172a)', fontSize: '13px' }}>
              {formatCurrency(draw.sharePriceCents || draw.sharePrice || 500)} / share
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              {isCaptain && draw.status === 'purchased' && sold > 0 && (
                <button
                  onClick={async () => { try { await lotteryAPI.closeDraw(clubId, draw.localDrawId || draw.id); loadData(); } catch (e) { alert(e.response?.data?.error || 'Failed'); } }}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #3b82f6', backgroundColor: 'transparent', color: '#3b82f6', fontSize: '12px', cursor: 'pointer' }}
                >
                  Close & Buy Tickets
                </button>
              )}
              <button
                disabled={past || full || draw.status === 'closed'}
                onClick={() => setBuyModalDraw(draw)}
                style={{
                  padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px', fontWeight: '600',
                  cursor: past || full || draw.status === 'closed' ? 'not-allowed' : 'pointer',
                  backgroundColor: past || full || draw.status === 'closed' ? '#3a3d45' : '#f0c040',
                  color: past || full || draw.status === 'closed' ? '#888' : '#1a1d23',
                }}
              >
                {draw.status === 'closed' ? 'Tickets Bought' : full ? 'Sold Out' : past ? 'Closed' : 'Buy Shares'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDrawsContent = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ color: '#fff', margin: 0, fontSize: '16px' }}>
          {embedded ? <><i className="fas fa-ticket" style={{ marginRight: '8px', color: '#f0c040' }}></i>Lotto Syndicate</> : 'Upcoming Draws'}
        </h3>
        {!embedded && isCaptain && (
          <button
            onClick={() => setShowCreateDraw(!showCreateDraw)}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #f0c040', backgroundColor: 'transparent', color: '#f0c040', fontSize: '13px', cursor: 'pointer' }}
          >
            + Add Draw
          </button>
        )}
      </div>

      {!embedded && showCreateDraw && (
        <div style={{ backgroundColor: '#1a1d23', borderRadius: '10px', padding: '16px', marginBottom: '16px', border: '1px solid #2a2d35' }}>
          <h4 style={{ margin: '0 0 12px', color: '#ccc', fontSize: '14px' }}>Pick a Draw to Enter</h4>
          <p style={{ color: '#888', fontSize: '12px', marginBottom: '12px' }}>Select a game, then choose an upcoming draw for your club to enter as a syndicate.</p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {availableGames.map((game) => (
              <button
                key={game.code}
                onClick={() => setCreateDrawData({ ...createDrawData, gameCode: createDrawData.gameCode === game.code ? '' : game.code })}
                style={{
                  padding: '6px 14px', borderRadius: '20px', border: 'none', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  backgroundColor: createDrawData.gameCode === game.code ? getGameColor(game.code) : '#22252d',
                  color: createDrawData.gameCode === game.code ? '#fff' : '#999',
                }}
              >
                {game.name}
              </button>
            ))}
          </div>
          {createDrawData.gameCode && (() => {
            const enteredIds = new Set(draws.filter(d => d.localDrawId).map(d => d.drawId || d.externalDrawId));
            const available = draws.filter(d => d.gameCode === createDrawData.gameCode && !d.localDrawId && !enteredIds.has(d.drawId));
            return available.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {available.slice(0, 3).map(d => (
                  <div key={d.drawId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', backgroundColor: '#22252d', borderRadius: '8px' }}>
                    <div>
                      <span style={{ color: '#ccc', fontSize: '14px', fontWeight: 600 }}>
                        {new Date(d.drawDate).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}
                      </span>
                      {d.drawNumber && <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>Draw #{d.drawNumber}</span>}
                      {d.jackpotCents > 0 && <span style={{ color: '#4ade80', fontSize: '13px', fontWeight: 600, marginLeft: '8px' }}>{formatJackpot(d.jackpotCents)}</span>}
                    </div>
                    <button
                      onClick={() => handleCreateDraw(d)}
                      disabled={createDrawLoading}
                      style={{ padding: '6px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#f0c040', color: '#1a1d23', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      {createDrawLoading ? '...' : 'Enter'}
                    </button>
                  </div>
                ))}
              </div>
            ) : <p style={{ color: '#666', fontSize: '13px' }}>No upcoming draws available for this game, or all have been entered.</p>;
          })()}
          {createDrawError && <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '8px' }}>{createDrawError}</div>}
        </div>
      )}

      {draws.length === 0 ? (
        !embedded && (
          <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#1a1d23', borderRadius: '10px', border: '1px solid #2a2d35' }}>
            <p style={{ color: '#888' }}>{isCaptain ? 'No upcoming draws. Use "+ Add Draw" to enter one.' : 'No upcoming draws. The captain can add draws for the club.'}</p>
          </div>
        )
      ) : (() => {
        const sorted = [...draws].sort((a, b) => new Date(a.drawDate) - new Date(b.drawDate));
        const grouped = {};
        sorted.forEach(d => {
          const code = d.gameCode || 'unknown';
          if (!grouped[code]) grouped[code] = [];
          grouped[code].push(d);
        });
        const gameOrder = Object.keys(grouped).sort((a, b) => new Date(grouped[a][0].drawDate) - new Date(grouped[b][0].drawDate));

        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
            {gameOrder.map(code => {
              const gameDraws = grouped[code];
              const nextDraw = gameDraws[0];
              const futureDraws = gameDraws.slice(1);
              const isExpanded = expandedGames[code];

              return (
                <div key={code}>
                  {renderDrawCard(nextDraw)}
                  {!embedded && futureDraws.length > 0 && (
                    <>
                      <button
                        onClick={() => setExpandedGames(prev => ({ ...prev, [code]: !prev[code] }))}
                        style={{
                          width: '100%', padding: '6px', marginTop: '4px', border: 'none', borderRadius: '6px',
                          backgroundColor: 'transparent', color: '#666', fontSize: '12px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                        }}
                      >
                        {isExpanded ? '▲ Hide' : '▼ Show'} {futureDraws.length} more
                      </button>
                      {isExpanded && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                          {futureDraws.map(d => renderDrawCard(d, true))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );

  if (loading) {
    return embedded ? null : <div className="fund-loading">Loading lotto data...</div>;
  }

  // In embedded mode, only show the draws section (no tabs)
  if (embedded) {
    if (draws.length === 0) return null;
    return (
      <div>
        {/* Draws content rendered below */}
        {renderDrawsContent()}
        {buyModalDraw && (
          <BuySharesModal
            draw={buyModalDraw}
            clubId={clubId}
            onClose={() => setBuyModalDraw(null)}
            onSuccess={() => { setBuyModalDraw(null); loadData(); }}
            currentUser={currentUser}
          />
        )}
      </div>
    );
  }

  const tabs = [
    { key: 'draws', label: 'Upcoming Draws' },
    { key: 'shares', label: 'My Shares' },
    { key: 'results', label: 'Results' },
  ];

  return (
    <div>
      <h2 style={{ color: '#fff', marginBottom: '4px' }}>Club Lotto</h2>
      <p style={{ color: '#888', fontSize: '14px', marginTop: 0, marginBottom: '20px' }}>
        Enter lottery draws as a syndicate and split the winnings.
      </p>

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #2a2d35' }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 16px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
              color: activeTab === tab.key ? '#f0c040' : '#888', fontSize: '14px',
              borderBottom: activeTab === tab.key ? '2px solid #f0c040' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Draws Tab */}
      {activeTab === 'draws' && renderDrawsContent()}

      {/* My Shares Tab */}
      {activeTab === 'shares' && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '16px', marginBottom: '12px' }}>My Shares</h3>
          {myShares.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#1a1d23', borderRadius: '10px', border: '1px solid #2a2d35' }}>
              <p style={{ color: '#888' }}>You have no shares yet. Buy shares in upcoming draws to participate.</p>
            </div>
          ) : (
            <div style={{ backgroundColor: '#1a1d23', borderRadius: '10px', border: '1px solid #2a2d35', overflow: 'hidden' }}>
              <div style={{ display: 'flex', padding: '10px 16px', borderBottom: '1px solid #2a2d35', backgroundColor: '#22252d' }}>
                <span style={{ flex: 1, fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Draw</span>
                <span style={{ flex: 1, fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Shares</span>
                <span style={{ flex: 1, fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Cost</span>
                <span style={{ flex: 1, fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Winnings</span>
              </div>
              {myShares.map((share, idx) => (
                <div key={share.id || idx} style={{ display: 'flex', padding: '12px 16px', borderBottom: '1px solid #1f2229' }}>
                  <span style={{ flex: 1, fontSize: '14px' }}>
                    <span style={{ color: getGameColor(share.draw?.gameCode || share.gameCode), fontWeight: '600' }}>
                      {share.draw?.gameName || share.gameName || 'Draw'}
                    </span>
                    <br />
                    <span style={{ color: '#888', fontSize: '12px' }}>
                      {(share.draw?.drawDate || share.drawDate) ? new Date(share.draw?.drawDate || share.drawDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : ''}
                    </span>
                  </span>
                  <span style={{ flex: 1, fontSize: '14px', color: '#ccc' }}>{share.shares || 1}</span>
                  <span style={{ flex: 1, fontSize: '14px', color: '#ccc' }}>{formatCurrency(share.costCents || 0)}</span>
                  <span style={{ flex: 1, fontSize: '14px', color: (share.winningsCents || 0) > 0 ? '#4ade80' : '#888', fontWeight: (share.winningsCents || 0) > 0 ? '600' : '400' }}>
                    {(share.winningsCents || 0) > 0 ? formatCurrency(share.winningsCents) : '-'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results Tab */}
      {activeTab === 'results' && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '16px', marginBottom: '12px' }}>Recent Results</h3>
          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#1a1d23', borderRadius: '10px', border: '1px solid #2a2d35' }}>
              <p style={{ color: '#888' }}>No results yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {history.map((draw, idx) => {
                const winNums = new Set(draw.winningNumbers || []);
                const suppNums = new Set(draw.supplementaryNumbers || []);
                const rawNums = draw.numbersSelected || [];
                const allGames = rawNums.length > 0 && Array.isArray(rawNums[0]) ? rawNums : (rawNums.length > 0 ? [rawNums] : []);
                const myShare = myShares.find(s => (s.draw?.id || s.drawId) === draw.id);

                return (
                  <div key={draw.id || idx} style={{ backgroundColor: '#1a1d23', borderRadius: '10px', border: '1px solid #2a2d35', padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                      <span>
                        {getGameLogo(draw.gameCode) ? (
                          <img src={getGameLogo(draw.gameCode)} alt={draw.gameName} style={{ height: '24px', objectFit: 'contain' }} />
                        ) : (
                          <span style={{ color: getGameColor(draw.gameCode), fontWeight: '600', fontSize: '15px' }}>{draw.gameName || draw.gameCode || 'Draw'}</span>
                        )}
                      </span>
                      <span style={{ color: '#888', fontSize: '13px' }}>
                        {draw.drawDate ? new Date(draw.drawDate).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        {draw.drawNumber ? ` - Draw #${draw.drawNumber}` : ''}
                      </span>
                    </div>

                    {draw.winningNumbers && draw.winningNumbers.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <span style={{ color: '#888', fontSize: '13px' }}>Winning:</span>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          {draw.winningNumbers.map((n) => renderNumberBall(n))}
                          {(draw.supplementaryNumbers || []).map((n) => renderNumberBall(n, true))}
                        </div>
                      </div>
                    )}

                    {allGames.length > 0 && (
                      <div style={{ marginBottom: '12px' }}>
                        <span style={{ color: '#888', fontSize: '13px' }}>Your {allGames.length} Game{allGames.length !== 1 ? 's' : ''}:</span>
                        {allGames.map((game, gIdx) => {
                          if (!Array.isArray(game)) return null;
                          const gMainMatches = game.filter(n => winNums.has(n)).length;
                          const gSuppMatches = game.filter(n => suppNums.has(n)).length;
                          return (
                            <div key={gIdx} style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                              <span style={{ color: '#666', fontSize: '10px', minWidth: '28px' }}>#{gIdx + 1}</span>
                              {game.map((n, nIdx) => (
                                <span key={nIdx} style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 26, height: 26, borderRadius: '50%', fontSize: 10, fontWeight: 700,
                                  backgroundColor: winNums.has(n) ? '#22c55e' : suppNums.has(n) ? '#3b82f6' : '#374151',
                                  color: winNums.has(n) || suppNums.has(n) ? '#fff' : '#888',
                                  border: winNums.has(n) ? '2px solid #4ade80' : suppNums.has(n) ? '2px solid #60a5fa' : '2px solid transparent',
                                }}>{n}</span>
                              ))}
                              <span style={{ fontSize: '11px', color: gMainMatches > 0 ? '#4ade80' : '#666', marginLeft: '4px' }}>
                                {gMainMatches > 0 ? `${gMainMatches}${gSuppMatches > 0 ? `+${gSuppMatches}s` : ''}` : '-'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Member share breakdown */}
                    {draw.memberShares && draw.memberShares.length > 0 && (
                      <div style={{ marginTop: '10px', padding: '10px 12px', borderRadius: '8px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div style={{ color: '#666', fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, marginBottom: '6px' }}>Syndicate Members</div>
                        {draw.memberShares.map((ms, mIdx) => {
                          const isMe = currentUser && ms.userId === currentUser.id;
                          return (
                            <div key={mIdx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: mIdx < draw.memberShares.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}>
                              <div>
                                <span style={{ color: isMe ? '#fff' : '#ccc', fontSize: '13px', fontWeight: isMe ? 600 : 400 }}>{ms.name || 'Member'}{isMe ? ' (you)' : ''}</span>
                                <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>{ms.shares} share{ms.shares !== 1 ? 's' : ''}</span>
                              </div>
                              <span style={{ fontWeight: 600, fontSize: '13px', color: (ms.winningsCents || 0) > 0 ? '#4ade80' : '#666' }}>
                                {(ms.winningsCents || 0) > 0 ? formatCurrency(ms.winningsCents) : '-'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {(draw.totalWinningsCents || 0) > 0 && (
                      <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', backgroundColor: 'rgba(74,222,128,0.1)', color: '#4ade80', fontSize: '14px', fontWeight: '600', textAlign: 'center' }}>
                        Syndicate Total: {formatCurrency(draw.totalWinningsCents)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Buy Shares Modal */}
      {buyModalDraw && (
        <BuySharesModal
          draw={buyModalDraw}
          clubId={clubId}
          onClose={() => setBuyModalDraw(null)}
          onSuccess={handleBuySuccess}
        />
      )}
    </div>
  );
}

export default ClubLotto;
